import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { GraduationCap, Pencil, X, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useStudentFriendships } from '../../hooks/useStudents';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { queryKeys } from '../../lib/queryClient';

export function ClassAssignmentPanel({ student, canEdit }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isChanging, setIsChanging] = useState(false);
  const [showGroupConfirm, setShowGroupConfirm] = useState(false);
  const [pendingClassUuid, setPendingClassUuid] = useState(undefined);

  const { data: allocationData, isLoading: classesLoading } = useQuery({
    queryKey: ['class-allocation', student.course],
    queryFn: () => api.classAllocation.getData(student.course),
    enabled: !!student.course,
  });

  const { data: friendships } = useStudentFriendships(student.uuid);

  const groupMembers = useMemo(() => {
    if (!friendships || !Array.isArray(friendships)) return [];
    const others = new Map();
    for (const f of friendships) {
      if (f.student1 !== student.uuid) others.set(f.student1, f.student1_name);
      if (f.student2 !== student.uuid) others.set(f.student2, f.student2_name);
    }
    return Array.from(others.entries()).map(([uuid, name]) => ({ uuid, name }));
  }, [friendships, student.uuid]);

  const isInGroup = groupMembers.length > 0;
  const allGroupUuids = [student.uuid, ...groupMembers.map((m) => m.uuid)];

  const availableClasses = useMemo(() => {
    if (!allocationData?.classes) return [];
    return allocationData.classes.filter((cg) => {
      if (cg.is_cinnire_class) return student.is_cinnire;
      return !student.is_cinnire;
    });
  }, [allocationData, student.is_cinnire]);

  const pendingClassName = useMemo(() => {
    if (!pendingClassUuid) return null;
    return availableClasses.find((c) => c.uuid === pendingClassUuid)?.name ?? null;
  }, [pendingClassUuid, availableClasses]);

  const moveMutation = useMutation({
    mutationFn: ({ uuids, classGroupUuid }) =>
      api.classAllocation.move(uuids, classGroupUuid),
    onSuccess: (_, { uuids, classGroupUuid }) => {
      const className = availableClasses.find((c) => c.uuid === classGroupUuid)?.name;
      toast({
        title: classGroupUuid
          ? t('students.class.moveSuccess', { class: className })
          : t('students.class.unassignSuccess'),
        description: isInGroup && uuids.length > 1
          ? t('students.class.groupMovedDesc', { count: uuids.length, class: className })
          : undefined,
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.students.detail(student.uuid) });
      queryClient.invalidateQueries({ queryKey: ['class-allocation', student.course] });
      queryClient.invalidateQueries({ queryKey: ['class-allocation', 'summary'] });
      setIsChanging(false);
      setShowGroupConfirm(false);
      setPendingClassUuid(undefined);
    },
    onError: (error) => {
      const msg = error?.response?.data?.error || error?.response?.data?.details?.[0]?.reason;
      toast({
        title: t('students.class.moveError'),
        description: msg,
        variant: 'destructive',
      });
    },
  });

  const initiateMove = (classGroupUuid) => {
    if (isInGroup) {
      setPendingClassUuid(classGroupUuid ?? null);
      setShowGroupConfirm(true);
    } else {
      moveMutation.mutate({ uuids: [student.uuid], classGroupUuid: classGroupUuid ?? null });
    }
  };

  const confirmMove = () => {
    moveMutation.mutate({ uuids: allGroupUuids, classGroupUuid: pendingClassUuid ?? null });
  };

  const cancelConfirm = () => {
    setShowGroupConfirm(false);
    setPendingClassUuid(undefined);
  };

  return (
    <Card>
      <CardHeader className="bg-muted/40 border-b border-border py-3 px-4">
        <CardTitle className="flex items-center text-sm font-semibold text-foreground">
          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#F28482]/10 mr-3">
            <GraduationCap className="h-4 w-4 text-[#F28482]" />
          </span>
          {t('students.class.currentAssignment')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        {!isChanging && !showGroupConfirm && (
          <div className="flex items-center justify-between">
            <div>
              {student.class_group ? (
                <p className="font-medium">{student.class_group_name || student.class_group}</p>
              ) : (
                <Badge variant="outline">{t('students.class.unassigned')}</Badge>
              )}
              {student.course_name && (
                <p className="text-xs text-muted-foreground mt-0.5">{student.course_name}</p>
              )}
            </div>

            {canEdit && student.course && (
              <div className="flex items-center space-x-2">
                {student.class_group && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => initiateMove(null)}
                    disabled={moveMutation.isPending}
                  >
                    <X className="h-4 w-4 mr-1" />
                    {isInGroup ? t('students.class.removeGroup') : t('students.class.remove')}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsChanging(true)}
                  disabled={moveMutation.isPending}
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  {t('students.class.changeClass')}
                </Button>
              </div>
            )}
          </div>
        )}

        {isChanging && !showGroupConfirm && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('students.class.selectPrompt')}</p>
            {classesLoading ? (
              <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
            ) : (
              <div className="flex items-center gap-2">
                <Select
                  onValueChange={(value) => {
                    initiateMove(value === '__unassigned__' ? null : value);
                  }}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder={`— ${t('students.class.unassigned')} —`} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__unassigned__">
                      — {t('students.class.unassigned')} —
                    </SelectItem>
                    {availableClasses.map((cg) => {
                      const isCurrent = cg.uuid === student.class_group;
                      const spotsNeeded = isInGroup
                        ? allGroupUuids.filter((uuid) => {
                            const s = allocationData?.students?.find((s) => s.uuid === uuid);
                            return !s || s.class_group_uuid !== cg.uuid;
                          }).length
                        : 1;
                      const isFull = cg.available_spots < spotsNeeded && !isCurrent;
                      return (
                        <SelectItem key={cg.uuid} value={cg.uuid} disabled={isFull}>
                          {cg.name}
                          {cg.teacher_name ? ` · ${cg.teacher_name}` : ''}
                          {' '}· {cg.current_enrollment}/{cg.capacity}
                          {isFull ? ` (${t('classAllocation.full')})` : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0 shrink-0 text-muted-foreground hover:text-foreground"
                  onClick={() => setIsChanging(false)}
                  aria-label={t('common.cancel')}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}

        {showGroupConfirm && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-3">
            <div className="flex items-start space-x-2">
              <Users className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-amber-900">
                  {pendingClassUuid
                    ? t('students.class.groupMoveConfirm', {
                        count: allGroupUuids.length,
                        class: pendingClassName,
                      })
                    : t('students.class.groupUnassignConfirm', {
                        count: allGroupUuids.length,
                      })}
                </p>
                <div className="text-xs text-amber-700 space-y-0.5">
                  <p className="font-medium">{t('students.housing.groupMembers')}:</p>
                  <p>{student.first_name} {student.last_name}</p>
                  {groupMembers.map((m) => (
                    <p key={m.uuid}>{m.name}</p>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button size="sm" onClick={confirmMove} disabled={moveMutation.isPending}>
                {t('common.yes')}
              </Button>
              <Button variant="ghost" size="sm" onClick={cancelConfirm} disabled={moveMutation.isPending}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ClassAssignmentPanel;
